import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { usePopoverAlign } from '../hooks/usePopoverAlign'
import {
  addDays,
  fmtLongDate,
  fmtShortDate,
  monthName,
  monthStart,
  nextMonthStart,
  prevMonthStart,
  sundayOf,
  WEEKDAYS_SHORT,
  type DatePreset,
  type LocalDate,
} from '../time'

/** The filter's value: a preset id (`any` = no filter), or `custom` with the picked days (both inclusive). */
export interface DateRangeValue {
  id: string
  from?: LocalDate
  to?: LocalDate
}

export interface DateRangeFilterProps {
  /** "Booked" / "Date". */
  label: string
  value: DateRangeValue
  presets: readonly DatePreset[]
  /** The clinic's today: the calendar's "today" mark and the future limit. */
  today: LocalDate
  /** Allow picking days after today (the appointment view). */
  allowFuture?: boolean
  onChange: (value: DateRangeValue) => void
}

const ANY: DateRangeValue = { id: 'any' }

/** "Oct 4 – Oct 10" for a custom range. */
export const customLabel = (from: LocalDate, to: LocalDate) => `${fmtShortDate(from)} – ${fmtShortDate(to)}`

/**
 * Date filter with presets and a click-start, click-end range calendar (mockup `DateRangeFilter.tsx`), on clinic-local
 * dates so the browser's time zone never matters. Future days are disabled unless `allowFuture`. As in `FilterMenu`,
 * the clear button sits beside the trigger, not inside it (Frontend §2.4).
 */
export function DateRangeFilter({ label, value, presets, today, allowFuture = false, onChange }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false)
  const { popRef, alignRight } = usePopoverAlign(open)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [view, setView] = useState(() => monthStart(today))
  const [start, setStart] = useState<LocalDate | null>(null)
  const [hover, setHover] = useState<LocalDate | null>(null)

  const close = () => {
    setOpen(false)
    setStart(null)
    setHover(null)
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setStart(null)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        setStart(null)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const days = useMemo(() => {
    const first = sundayOf(view)
    const next = nextMonthStart(view)
    const out: LocalDate[] = []
    for (let d = first; d < next || out.length % 7 !== 0; d = addDays(d, 1)) out.push(d)
    return out
  }, [view])

  const pick = (d: LocalDate) => {
    if (!start) {
      setStart(d)
      return
    }
    const [a, b] = d < start ? [d, start] : [start, d]
    onChange({ id: 'custom', from: a, to: b })
    close()
  }

  const lo = start ? (hover && hover < start ? hover : start) : (value.from ?? null)
  const hi = start ? (hover && hover > start ? hover : start) : (value.to ?? null)
  const inRange = (d: LocalDate) => lo !== null && hi !== null && d >= lo && d <= hi
  const canNext = allowFuture || nextMonthStart(view) <= today
  const active = value.id !== 'any'
  const presetLabel = presets.find((p) => p.id === value.id)?.label
  const shown =
    value.id === 'custom' && value.from && value.to
      ? customLabel(value.from, value.to)
      : (presetLabel ?? presets[0]?.label ?? '')

  return (
    <div className="date-filter" ref={ref}>
      <div className={`filter-trigger ${active ? 'active' : ''}`}>
        <button
          ref={triggerRef}
          type="button"
          className="ft-main"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={`${label}: ${shown}`}
          onClick={() => {
            if (open) close()
            else setOpen(true)
          }}
        >
          <CalendarDays aria-hidden />
          <span className="ft-label">{label}:</span> {shown}
          {!active && <ChevronDown aria-hidden />}
        </button>
        {active && (
          <button
            type="button"
            className="ft-clear"
            aria-label="Clear date filter"
            onClick={() => {
              onChange(ANY)
            }}
          >
            <X aria-hidden />
          </button>
        )}
      </div>
      {open && (
        <div
          className={`df-pop ${alignRight ? 'align-right' : ''}`}
          ref={popRef}
          role="dialog"
          aria-label={`${label} date range`}
        >
          <div className="df-presets">
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={value.id === p.id}
                onClick={() => {
                  onChange(p.id === 'any' ? ANY : { id: p.id })
                  close()
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="df-cal">
            <div className="cal-head" style={{ marginBottom: 8 }}>
              <div className="cal-title" style={{ fontSize: 14 }}>
                {monthName(view)} {view.slice(0, 4)}
              </div>
              <div className="cal-nav">
                <button
                  type="button"
                  onClick={() => {
                    setView(prevMonthStart(view))
                  }}
                  aria-label="Previous month"
                >
                  <ChevronLeft aria-hidden />
                </button>
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={() => {
                    setView(nextMonthStart(view))
                  }}
                  aria-label="Next month"
                >
                  <ChevronRight aria-hidden />
                </button>
              </div>
            </div>
            {/* The hover highlight is a mouse-only preview; clicks on the day buttons do the work. */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className="rc-grid"
              onMouseLeave={() => {
                setHover(null)
              }}
            >
              {WEEKDAYS_SHORT.map((d) => (
                <div key={d} className="cal-dow" aria-hidden>
                  {d.slice(0, 2)}
                </div>
              ))}
              {days.map((d) => {
                const inMonth = d.slice(0, 7) === view.slice(0, 7)
                const future = !allowFuture && d > today
                const edge = d === lo || d === hi
                const cls = ['rc-day', inRange(d) && 'in', edge && 'edge', d === today && 'today']
                  .filter(Boolean)
                  .join(' ')
                return (
                  <button
                    key={d}
                    type="button"
                    className={cls}
                    disabled={future || !inMonth}
                    style={{ visibility: inMonth ? 'visible' : 'hidden' }}
                    aria-label={fmtLongDate(d)}
                    onMouseEnter={() => {
                      setHover(d)
                    }}
                    onClick={() => {
                      pick(d)
                    }}
                  >
                    {Number(d.slice(8, 10))}
                  </button>
                )
              })}
            </div>
            <div className="hint" style={{ marginTop: 8 }} aria-live="polite">
              {start ? 'Now pick the end date' : 'Pick a start date for a custom range'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
